/**
 * TargetMuscle Component
 * 
 * Interactive muscle targeting visualization for exercises.
 * Features:
 * - SVG-based anatomy diagram with hover tooltips
 * - Exercise selection with muscle highlighting
 * - Material-UI integration with theme support
 * - Responsive design
 * 
 * Future Enhancement Areas:
 * - Add more exercises and muscle groups
 * - Include exercise instructions and form tips
 * - Add muscle group filtering
 * - Integrate with workout planning features
 * - Add animation transitions for muscle highlighting
 * - Support for custom exercise creation
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactComponent as MuscleGroupSvg } from '../assets/muscle_outlined_grouped_compressed.svg';
import { exerciseData } from '../data/exerciseData';
import { getBilateralMuscles, formatMuscleName, generateHighlightCSS } from '../utils/muscleUtils';



const Tooltip = styled(Box)(({ theme }) => ({
  position: 'absolute',
  display: 'none',
  padding: '8px 12px',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  color: 'white',
  borderRadius: '6px',
  fontSize: '14px',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
  zIndex: 10,
}));

const LegendItem = styled(ListItem)(({ theme }) => ({
  padding: '4px 0',
}));

const ColorIndicator = styled(Box)(({ color, theme }) => ({
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: color,
  border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'}`,
  minWidth: 20,
  marginRight: theme.spacing(1)
}));

const TargetMuscle = () => {
  const theme = useTheme();
  const [selectedExercise, setSelectedExercise] = useState('bench-press');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [highlightedMuscles, setHighlightedMuscles] = useState({ primary: [], secondary: [] });
  const tooltipRef = useRef(null);





  const highlightMuscles = React.useCallback((exerciseId) => {
    if (!exerciseId || !exerciseData[exerciseId]) {
      setHighlightedMuscles({ primary: [], secondary: [] });
      return;
    }
    
    const exercise = exerciseData[exerciseId];
    
    // Get bilateral muscles for primary and secondary targets (reduce multiple calls)
    const expandedPrimary = getBilateralMuscles(exercise.primary);
    const expandedSecondary = getBilateralMuscles(exercise.secondary);
    
    setHighlightedMuscles({
      primary: expandedPrimary,
      secondary: expandedSecondary
    });
  }, [getBilateralMuscles]);

  const handleExerciseChange = (event) => {
    setSelectedExercise(event.target.value);
  };

  const handleSvgMouseOver = (event) => {
    const target = event.target;
    if (target.id) {
      const muscleName = formatMuscleName(target.id);
      setTooltipContent(muscleName);
      setTooltipVisible(true);
    }
  };

  const handleSvgMouseMove = (event) => {
    setTooltipPosition({ x: event.pageX + 10, y: event.pageY + 10 });
  };

  const handleSvgMouseOut = () => {
    setTooltipVisible(false);
  };



  useEffect(() => {
    highlightMuscles(selectedExercise);
  }, [selectedExercise, highlightMuscles]);





  const currentExercise = exerciseData[selectedExercise];
  const categories = [...new Set(Object.values(exerciseData).map(ex => ex.category))];

  const muscleCategories = [
    { name: 'Core', color: '#fff9e6', muscles: ['abs', 'oblique', 'rectus_abdominis'] },
    { name: 'Chest', color: '#e6f3ff', muscles: ['chest'] },
    { name: 'Back', color: '#e8f4fd', muscles: ['back', 'lower_back', 'lats', 'traps'] },
    { name: 'Shoulders', color: '#fff2e6', muscles: ['shoulder'] },
    { name: 'Arms', color: '#f0e6ff', muscles: ['biceps', 'triceps', 'forearms'] },
    { name: 'Legs', color: '#e6f7ff', muscles: ['quadriceps', 'hamstring', 'abductor'] },
    { name: 'Glutes', color: '#e6ffe6', muscles: ['glutes'] },
    { name: 'Calves', color: '#ffe6e6', muscles: ['calves'] },
    { name: 'Neck', color: '#f0f0f0', muscles: ['neck', 'neck_center'] }
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Tooltip
        ref={tooltipRef}
        sx={{
          display: tooltipVisible ? 'block' : 'none',
          left: tooltipPosition.x,
          top: tooltipPosition.y
        }}
      >
        {tooltipContent}
      </Tooltip>

      <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
        <Box textAlign="center" mb={4}>
          <Typography variant="h3" component="h1" gutterBottom color="primary" fontWeight="bold">
            Exercise Muscle Target Map
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select an exercise or hover over a muscle to learn more.
          </Typography>
        </Box>

        <Grid container spacing={2} justifyContent="center" mb={4}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel id="category-select-label">Muscle Group</InputLabel>
              <Select
                labelId="category-select-label"
                value={selectedCategory}
                label="Muscle Group"
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedExercise(''); // Reset exercise selection
                }}
              >
                <MenuItem value="">All Muscle Groups</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel id="exercise-select-label">Select Exercise</InputLabel>
              <Select
                labelId="exercise-select-label"
                value={selectedExercise}
                label="Select Exercise"
                onChange={handleExerciseChange}
              >
                {Object.entries(exerciseData)
                  .filter(([key, exercise]) => 
                    !selectedCategory || exercise.category === selectedCategory
                  )
                  .map(([key, exercise]) => (
                    <MenuItem key={key} value={key}>
                      {exercise.name}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={4} alignItems="flex-start">
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom>
                Human Anatomy - 11 Key Muscle Groups
              </Typography>
              <style>
                {generateHighlightCSS(highlightedMuscles)}
                {`
                  .muscle-group {
                    cursor: pointer;
                    transition: all 0.3s ease;
                  }
                  .muscle-group:hover {
                    opacity: 0.8;
                    filter: brightness(1.1);
                    stroke-width: 3;
                  }
                  .muscle-group.highlighted {
                    filter: brightness(1.2);
                    stroke-width: 3;
                    stroke: #1976d2;
                  }
                `}
              </style>
              
              {/* Muscle Group Legend */}
              <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {muscleCategories.map((category) => (
                  <Chip
                    key={category.name}
                    label={category.name}
                    variant={selectedCategory === category.name ? "filled" : "outlined"}
                    color={selectedCategory === category.name ? "primary" : "default"}
                    size="small"
                    onClick={() => {
                      setSelectedCategory(selectedCategory === category.name ? '' : category.name);
                      setSelectedExercise('');
                    }}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: selectedCategory !== category.name ? category.color : undefined,
                      '&:hover': {
                        backgroundColor: category.color,
                        opacity: 0.8
                      }
                    }}
                  />
                ))}
              </Box>
            <Box display="flex" justifyContent="center">
              <Box
                component={MuscleGroupSvg}
                sx={{
                  width: '100%',
                  maxWidth: '500px',
                  height: 'auto',
                  maxHeight: '600px',
                  cursor: 'pointer',
                  margin: '0 auto',
                  display: 'block',
                  '& path, & g': {
                    transition: 'all 0.3s ease',
                  },
                  '& .muscle-group:hover': {
                    filter: 'brightness(1.1)',
                    strokeWidth: '2px',
                  }
                }}
                onClick={(e) => {
                  const target = e.target;
                  const muscleId = target.id;
                  if (muscleId) {
                    const category = muscleCategories.find(cat => 
                      cat.muscles.includes(muscleId)
                    );
                    if (category) {
                      setSelectedCategory(category.name);
                      setSelectedExercise('');
                    }
                  }
                }}
                onMouseOver={handleSvgMouseOver}
                onMouseMove={handleSvgMouseMove}
                onMouseOut={handleSvgMouseOut}
              >
                {/* Dynamic highlighting styles */}
                <style>
                  {`
                    /* Default muscle group styling */
                    svg path, svg g {
                      fill: ${theme.palette.mode === 'dark' ? '#424242' : '#E0E0E0'};
                      stroke: ${theme.palette.mode === 'dark' ? '#616161' : '#BDBDBD'};
                      stroke-width: 0.5px;
                      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                      cursor: pointer;
                    }
                    
                    /* Hover effects */
                    svg path:hover, svg g:hover {
                      fill: ${theme.palette.mode === 'dark' ? '#616161' : '#BDBDBD'} !important;
                      stroke: ${theme.palette.mode === 'dark' ? '#757575' : '#9E9E9E'} !important;
                      stroke-width: 2px !important;
                    }
                    
                    /* Primary muscle highlighting with aesthetic colors */
                    ${highlightedMuscles.primary.map(muscle => 
                      `#${muscle}, #${muscle} path, #${muscle} > * { fill: #6366f1 !important; stroke: #4f46e5 !important; stroke-width: 3px !important; filter: drop-shadow(0 0 6px rgba(99, 102, 241, 0.4)) !important; }`
                    ).join(' ')}
                    
                    /* Secondary muscle highlighting with aesthetic colors */
                    ${highlightedMuscles.secondary.map(muscle => 
                      `#${muscle}, #${muscle} path, #${muscle} > * { fill: #f59e0b !important; stroke: #d97706 !important; stroke-width: 2px !important; filter: drop-shadow(0 0 4px rgba(245, 158, 11, 0.3)) !important; }`
                    ).join(' ')}
                    
                    /* Category selection highlighting */
                    ${selectedCategory ? muscleCategories.find(cat => cat.name === selectedCategory)?.muscles.map(muscle => 
                      `#${muscle} { filter: brightness(1.2) !important; stroke-width: 3px !important; stroke: #1976d2 !important; }`
                    ).join(' ') : ''}
                    
                    /* Responsive design */
                    @media (max-width: 768px) {
                      svg {
                        max-height: 400px !important;
                      }
                    }
                    
                    @media (max-width: 480px) {
                      svg {
                        max-height: 300px !important;
                      }
                    }
                  `}
                </style>
              </Box>
            </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3, 
                backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                height: 'fit-content'
              }}
            >
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Muscle Key
              </Typography>
              <List dense>
                <LegendItem>
                  <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                    <ColorIndicator color="#6366f1" />
                  </ListItemIcon>
                  <ListItemText primary="Primary" />
                </LegendItem>
                <LegendItem>
                  <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                    <ColorIndicator color="#f59e0b" />
                  </ListItemIcon>
                  <ListItemText primary="Secondary" />
                </LegendItem>
              </List>

              {currentExercise && (
                <Box mt={3} pt={2} borderTop={1} borderColor="divider">
                  <Box mb={2}>
                    <Chip 
                      label={currentExercise.category} 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                    {currentExercise.name}
                  </Typography>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.primary" fontWeight="medium">
                      Primary Muscles:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentExercise.primary.length > 0 
                        ? getBilateralMuscles(currentExercise.primary).map(formatMuscleName).join(', ') 
                        : 'None'
                      }
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.primary" fontWeight="medium">
                      Secondary Muscles:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentExercise.secondary.length > 0 
                        ? getBilateralMuscles(currentExercise.secondary).map(formatMuscleName).join(', ') 
                        : 'None'
                      }
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default TargetMuscle;